import { ParseError } from './errors';

/** Big-endian cursor over a byte buffer, following TLS presentation-language conventions. */
export class Reader {
  pos: number;

  constructor(
    readonly buf: Uint8Array,
    pos = 0,
  ) {
    this.pos = pos;
  }

  get remaining(): number {
    return this.buf.length - this.pos;
  }

  get eof(): boolean {
    return this.pos >= this.buf.length;
  }

  private need(n: number): void {
    if (n < 0 || this.pos + n > this.buf.length) {
      throw new ParseError(`unexpected end of data: need ${n} byte(s), have ${this.remaining}`, this.pos);
    }
  }

  u8(): number {
    this.need(1);
    return this.buf[this.pos++]!;
  }

  u16(): number {
    this.need(2);
    const v = (this.buf[this.pos]! << 8) | this.buf[this.pos + 1]!;
    this.pos += 2;
    return v;
  }

  u24(): number {
    this.need(3);
    const v = (this.buf[this.pos]! << 16) | (this.buf[this.pos + 1]! << 8) | this.buf[this.pos + 2]!;
    this.pos += 3;
    return v;
  }

  u32(): number {
    this.need(4);
    const v =
      this.buf[this.pos]! * 0x1000000 +
      ((this.buf[this.pos + 1]! << 16) | (this.buf[this.pos + 2]! << 8) | this.buf[this.pos + 3]!);
    this.pos += 4;
    return v;
  }

  u40(): number {
    this.need(5);
    const hi = this.u8();
    return hi * 0x100000000 + this.u32();
  }

  u64(): bigint {
    this.need(8);
    const hi = BigInt(this.u32());
    const lo = BigInt(this.u32());
    return (hi << 32n) | lo;
  }

  /** u64 as a JS number; throws if it does not fit in 2^53. Timestamps and tree sizes always do. */
  u64n(): number {
    const start = this.pos;
    const v = this.u64();
    if (v > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new ParseError(`uint64 value ${v} exceeds Number.MAX_SAFE_INTEGER`, start);
    }
    return Number(v);
  }

  /** Returns a view (not a copy) of the next n bytes. */
  bytes(n: number): Uint8Array {
    this.need(n);
    const v = this.buf.subarray(this.pos, this.pos + n);
    this.pos += n;
    return v;
  }

  /** Reads an `opaque<0..2^(8*lenBytes)-1>` vector: length prefix followed by that many bytes. */
  vec(lenBytes: 1 | 2 | 3 | 4): Uint8Array {
    const n = lenBytes === 1 ? this.u8() : lenBytes === 2 ? this.u16() : lenBytes === 3 ? this.u24() : this.u32();
    return this.bytes(n);
  }

  /** A bounded reader over the next n bytes; advances this reader past them. */
  sub(n: number): Reader {
    return new Reader(this.bytes(n));
  }

  /** A bounded reader over the next length-prefixed vector. */
  subVec(lenBytes: 1 | 2 | 3 | 4): Reader {
    return new Reader(this.vec(lenBytes));
  }

  expectEnd(what = 'structure'): void {
    if (!this.eof) {
      throw new ParseError(`${this.remaining} trailing byte(s) after ${what}`, this.pos);
    }
  }
}
